import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { WelletContext } from '../../context/WelletContext';
import { getQueryString } from '../../helpers/url';
import { fetchPickupDetails, fetchPickups } from '../../services/reservations/reservationsService';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import GeneralSelectorPicker from '../../components/SelectorPicker/GeneralSelectorPicker';
import Money from '../../components/Common/Money/Money';
import ButtonReservation from '../../components/Common/Button/ButtonReservation';
import {
  addProduct,
  resetTransportation,
  setTransportation,
} from '../../actions/reservationActions';
import { capitalizedWords } from '../../helpers/text';
import { sortByField } from '../../helpers/array';
import ChevronIcon from '../../components/icons/chevronIcon';
import { Skeleton } from '@mui/material';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import { format } from 'date-fns';

const Pickup = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const welletContext = useContext(WelletContext);
  const history = useHistory();
  const showId = getQueryString('showId');
  const eventId = getQueryString('eventId');
  const currency = useSelector((state) => state.currency.selected);
  const lang = useSelector((state) => state.app.language);
  const reservation = useSelector((state) => state.reservation);
  const [loading, setLoading] = useState(false);
  const [withTransport, setWithTransport] = useState(reservation.transfer);
  const [zones, setZones] = useState([]);
  const [pickups, setPickups] = useState([]);
  const [pickupsFilter, setPickupsFilter] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [selectedPickup, setSelectedPickup] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [pickupInfo, setPickupInfo] = useState(null);
  const [showCustomPickup, setShowCustomPickup] = useState(false);
  const [customPickup, setCustomPickup] = useState('');
  const [error, setError] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const getPickup = async () => {
    setLoading(true);
    try {
      const pickup = await fetchPickups(showId, 'TICKET', welletContext);
      setZones(
        [...new Set(pickup.map((item) => item.name))].map((desc) => ({
          value: desc,
          label: desc === 'CUSTOM' ? t('customPickup') : capitalizedWords(desc),
        })),
      );
      setPickups(pickup);

      // if (reservation.reservationTransferRequest) {
      //   const zone = pickup.find((z) =>
      //     z.pickups.some(
      //       (p) => p.id === reservation.reservationTransferRequest.trips[0].fromPickupName,
      //     ),
      //   );
      //   setSelectedZone({ value: zone.name, label: zone.name });
      //   const pick = zone.pickups.find(
      //     (p) => p.id === reservation.reservationTransferRequest.trips[0].fromPickupName,
      //   );
      //   setSelectedPickup({ value: pick.id, label: pick.name });
      // }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPickupDetails = async (pickupId) => {
    setLoadingDetails(true);
    try {
      const result = await fetchPickupDetails(
        eventId,
        pickupId,
        currency.shortForm,
        lang,
        welletContext,
      );
      setPickupInfo(result);

      if (result.length > 0) {
        setSelectedProduct(result[0]);
      }
    } catch (error) {
      console.error(error);
      setPickupInfo(null);
      //setSelectedPrice(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const getTotal = () => {
    if (!selectedProduct) return 0;
    return selectedProduct.prices.price * reservation.paxs;
  };

  const handleZoneSelect = (zone) => {
    setSelectedZone(zone);
    setSelectedPickup(null);
    setSelectedProduct(null);
    setError(false);

    const zoneData = pickups.find((p) => p.name === zone.value);

    if (!zoneData) return;

    if (zone.value === 'CUSTOM') {
      return handleSelectCustom(zoneData);
    }

    setShowCustomPickup(false);
    setCustomPickup(null);

    const items = zoneData.pickups ?? [];

    setPickupsFilter(
      items.map((item) => ({
        value: item.id,
        label: capitalizedWords(item.name),
      })),
    );
  };

  const handleSelectCustom = (item) => {
    setShowCustomPickup(true);
    setSelectedPickup({ value: item.name, label: item.name });
    getPickupDetails(item.name);
  };

  const handlePickupSelect = (p) => {
    setSelectedPickup(p);
    setSelectedProduct(null);
    getPickupDetails(p.value);
    setError(false);
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setError(false);
  };

  const handleContinue = () => {
    if (withTransport) {
      let transferInfo = {
        transferType: 'ROUNDTRIP',
      };

      transferInfo.trips = [];
      transferInfo.trips.push({
        fromPickupName: selectedPickup.label,
        fromPickupId: selectedPickup.value,
        transferDate: selectedProduct.departureDate,
      });

      dispatch(setTransportation(transferInfo));
      dispatch(
        addProduct({
          id: selectedProduct.id,
          quantity: reservation.paxs,
          extraPaxs: 0,
          specialType: 'TRANSFER',
          departureDate: selectedProduct.departureDate,
        }),
      );
    }

    let query = `?showId=${showId}&eventId=${eventId}`;

    history.push({
      pathname: '/reservation-check-tickets',
      search: query,
    });
  };

  const renderCustomPickup = () => {
    return (
      <div className='mt-2'>
        <div className='fw-semibold text-primary font-tiny mt-3 mb-1 ml-1'>
          {t('wherePickupCustom')}
        </div>
        <div className={`field-holder ${error ? 'error' : ''}`}>
          <div className='input-placeholder'>
            <textarea
              onChange={(e) => {
                setCustomPickup(e.target.value);
                setError(false);
              }}
            >
              {customPickup}
            </textarea>
            <span className='floating-placeholder '>{t('addressOrReference')}</span>
          </div>
          {error && (
            <div
              className='error-label text-center'
              style={{ position: 'relative', marginTop: '10px' }}
            >
              {t('customPickupError')}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPickupSelector = () => {
    return (
      <>
        {selectedZone && (
          <>
            <div className='fw-semibold text-primary font-tiny mt-3 mb-1 ml-1'>
              {t('pickupLocation')}
            </div>

            <GeneralSelectorPicker
              key={pickupsFilter[0]?.value}
              options={sortByField(pickupsFilter, 'label')}
              selectedValue={selectedPickup}
              onChange={handlePickupSelect}
              placeholder={t('select')}
            />
          </>
        )}

        {selectedPickup && (
          <>
            <div className='fw-semibold text-primary font-tiny mt-4'>
              {t('schedulesTransportation')}
            </div>
            {loadingDetails ? (
              <div className='pt-2'>
                <Skeleton
                  variant='rectangular'
                  sx={{
                    bgcolor: 'grey.900',
                    width: '100%',
                    borderRadius: '.4rem',
                    height: 88,
                  }}
                />
              </div>
            ) : pickupInfo === null ? (
              <div className='mt-2'>
                <ErrorBox title={t('pickupNotAvailable')} />
              </div>
            ) : (
              pickupInfo.map((item) => (
                <>
                  <div
                    key={item.id}
                    className={`btn-primary selector w-100 text-left line-height-1 py-2 mt-2 p-2  ${
                      selectedProduct.id !== item.id ? 'btn-outline' : ''
                    }`}
                    style={{ height: 'auto', borderRadius: '0.25rem' }}
                    onClick={() => handleSelectProduct(item)}
                  >
                    <div className='ml-1'>
                      <div className='fw-semibold font-normal'>{t('standardpickup')}</div>
                      <div className='fw-300 font-small mt-1'>
                        {format(new Date(item.departureDate), 'HH:mm')} -{' '}
                        {t('productType.EXTRAPAX.priceUnit')}
                      </div>
                      <div className='fw-semibold font-normal mt-3'>
                        {item.prices.price > 0 ? (
                          <Money value={item.prices.price} />
                        ) : (
                          t('included')
                        )}
                      </div>
                    </div>
                  </div>
                </>
              ))
            )}
          </>
        )}

        {error && (
          <div
            className='error-label text-center'
            style={{ position: 'relative', marginTop: '10px' }}
          >
            {t('selectAnOption')}
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    dispatch(resetTransportation());
  }, []);

  useEffect(() => {
    if (withTransport && pickups.length === 0) getPickup();
  }, [withTransport]);

  return (
    <>
      <>
        <Header />
        <ContentApp classes='pb-0'>
          <div className='fw-semibold text-primary font-tiny'>{t('transportInformation')}</div>
          <div className='row mt-3'>
            <div className='col pr-1'>
              <button
                style={{ lineHeight: '20px' }}
                className={`btn btn-primary selector w-100 text-left font-tiny ${
                  withTransport ? ' btn-outline' : ''
                }`}
                onClick={() => setWithTransport(false)}
              >
                {t('withoutTransportation')}
              </button>
            </div>
            <div className='col pl-1'>
              <button
                className={`btn btn-primary selector w-100 text-left font-tiny ${
                  !withTransport ? ' btn-outline' : ''
                }`}
                onClick={() => setWithTransport(true)}
              >
                {t('withTransportation')}
              </button>
            </div>
          </div>

          {withTransport && (
            <div className='card py-3 px-2 mt-2'>
              <div className='fw-semibold text-primary font-tiny mb-1 ml-1'>{t('zona')}</div>
              {loading && !selectedZone ? (
                <Skeleton
                  variant='rectangular'
                  sx={{
                    bgcolor: 'grey.900',
                    width: '100%',
                    borderRadius: '.4rem',
                    height: 61,
                  }}
                />
              ) : (
                <GeneralSelectorPicker
                  options={sortByField(zones, 'label')}
                  onChange={handleZoneSelect}
                  placeholder={t('select')}
                  selectedValue={selectedZone}
                />
              )}

              {showCustomPickup ? renderCustomPickup() : renderPickupSelector()}
            </div>
          )}
          <div
            className='position-fixed w-100'
            style={{
              bottom: 0,
              left: 0,
              zIndex: 2,
            }}
          >
            <div className='container'>
              <div className='row justify-content-center'>
                <div className='col-12 col-md-7 col-lg-6 p-0'>
                  <ButtonReservation
                    leftContent={
                      withTransport && selectedProduct ? (
                        <>
                          {t('total')} <Money value={getTotal()} />
                        </>
                      ) : (
                        t('continue')
                      )
                    }
                    rightContent={
                      withTransport && selectedProduct ? (
                        <>
                          {t('continue')} <ChevronIcon size={20} fillColor={'#000'} />
                        </>
                      ) : (
                        <ChevronIcon size={20} fillColor={'#000'} />
                      )
                    }
                    backgroundColor={
                      !withTransport || !selectedProduct ? 'var(--color-deep-gray)' : null
                    }
                    size='large'
                    className={`btn-primary`}
                    onClick={handleContinue}
                    fixed={true}
                  />
                </div>
              </div>
            </div>
          </div>
        </ContentApp>
      </>
    </>
  );
};

export default Pickup;
